import React from 'react';
import { connect } from 'react-redux';
import * as THREE from "three";
import SetObjectPositionAction from '../../actions/SetObjectPositionAction';
import SetObjectRotationAction from '../../actions/SetObjectRotationAction';
import SetObjectScaleAction from '../../actions/SetObjectScaleAction';
import SetObjectTransformAction from '../../actions/SetObjectTransformAction';
import TranslateObjectWithoutChildrenAction from '../../actions/TranslateObjectWithoutChildrenAction';
import Vector3Property from '../properties/Vector3Property';
import PropertyGroup from '../properties/PropertyGroup';
import SyncedProperty from '../properties/SyncedProperty';
import Engine from '../../classes/Engine';
import Utils from '../../classes/Utilities';
import { CustomEventTypes, ObjectInspectorSectionTypes, Space, TimelineEffectorTypes } from '../../globals';
import ARTracker from '../../classes/ARTracker';
import store, { StoreActions } from '../../state/store';
import { CameraExport, CameraImport } from '../Iconset';
import { TooltipKeys } from '../tooltips/TooltipKeys';
import AlignToViewAction from '../../actions/AlignToViewAction';
import MathUtils from '../../classes/utilities/MathUtils';
import Callout from "../../classes/Callout";
import TransformUtils from "../../classes/utilities/TransformUtils";
import Spacer from "../../components/layout/Spacer";
import ScopeCamera from '../../classes/Camera';
import ContextMenu from '../menus/ContextMenu';
import { areArraysEqual } from '../../classes/utilities/Misc';

let k = 0;

const childrenWorldPositions = new Map();

interface TransformComponentProps {
	objects: any[];
	snapSettings: { translation: number; rotation: number; scale: number; };
	readOnly?: boolean;
	noGroup?: boolean;
}

interface TransformComponentState {
	position: [number, number, number];
	rotation: [number, number, number];
	scale: [number, number, number];
	positionLocked: boolean;
	rotationLocked: boolean;
	scaleLocked: boolean;
}

class TransformComponent extends React.Component<TransformComponentProps, TransformComponentState>
{
	propertyType: any;
	objects: any[];
	initialPositions: THREE.Vector3[];
	initialRotations: THREE.Euler[];
	initialScales: THREE.Vector3[];
	positionMultivalue: Record<string, boolean>;
	rotationMultivalue: Record<string, boolean>;
	scaleMultivalue: Record<string, boolean>;
	displayScale: boolean;
	onlyEmptyObjectsSelected: boolean;

	constructor(props)
	{
		super(props);

		this.onPositionChanged = this.onPositionChanged.bind(this);
		this.onPositionCommitted = this.onPositionCommitted.bind(this);
		this.onTranslateWithoutChildrenGizmoControlMouseDown = this.onTranslateWithoutChildrenGizmoControlMouseDown.bind(this);
		this.onTranslateWithoutChildrenGizmoControlMouseMove = this.onTranslateWithoutChildrenGizmoControlMouseMove.bind(this);
		this.onTranslateWithoutChildrenGizmoControlMouseUp = this.onTranslateWithoutChildrenGizmoControlMouseUp.bind(this);
		this.onRotationChanged = this.onRotationChanged.bind(this);
		this.onRotationCommitted = this.onRotationCommitted.bind(this);
		this.onScaleChanged = this.onScaleChanged.bind(this);
		this.onScaleCommitted = this.onScaleCommitted.bind(this);
		this.handleHierarchyChanged = this.handleHierarchyChanged.bind(this);
		this.handlePositionChanged = this.handlePositionChanged.bind(this);
		this.handleRotationChanged = this.handleRotationChanged.bind(this);
		this.handleScaleChanged = this.handleScaleChanged.bind(this);
		this.handleWorldUnitChanged = this.handleWorldUnitChanged.bind(this);
		this.copy = this.copy.bind(this);
		this.paste = this.paste.bind(this);
		this.isMultiValues = this.isMultiValues.bind(this);
		this.handlePropertyChanged = this.handlePropertyChanged.bind(this);

		this.state =
		{
			position: [0, 0, 0],
			rotation: [0, 0, 0],
			scale: [0, 0, 0],
			positionLocked: false,
			rotationLocked: false,
			scaleLocked: false,
		}

		this.propertyType = ObjectInspectorSectionTypes.Transform;
		this.objects = props.objects;
		this.initialPositions = [];
		this.initialRotations = [];
		this.initialScales = [];

		this.positionMultivalue = {};
		this.rotationMultivalue = {};
		this.scaleMultivalue = {};
		this.displayScale = true;
		this.onlyEmptyObjectsSelected = false;
	}

	handlePositionChanged(e)
	{
		if (!e.detail || e.detail.sender !== this) this.initializePostionValues();
	}

	handleRotationChanged(e)
	{
		if (!e.detail || e.detail.sender !== this) this.initializeRotationValues();
	}

	handleScaleChanged(e)
	{
		if (!e.detail || e.detail.sender !== this) this.initializeScaleValues();
	}

	handleHierarchyChanged(e)
	{
		this.initializeValues();
	}

	handleWorldUnitChanged(e)
	{
		this.initializePostionValues();
		this.initializeScaleValues();
	}

	handlePropertyChanged(e)
	{
		this.updateInteractiveModeCameraRotationLock();
	}

	updateInteractiveModeCameraRotationLock()
	{
		if (this.props.objects.length !== 1) this.setState({ rotationLocked: false });
		const object = this.props.objects[0];
		if (object.userData.isInteractiveModeCamera && object.userData.interactiveCameraFocusPoint)
		{
			this.setState({ rotationLocked: true });
		}
		else 
		{
			this.setState({ rotationLocked: false });
		}
	}

	componentDidMount()
	{
		document.addEventListener(CustomEventTypes.hierarchyChangedEvent, this.handleHierarchyChanged);
		document.addEventListener(CustomEventTypes.selectedObjectPositionChanged, this.handlePositionChanged);
		document.addEventListener(CustomEventTypes.selectedObjectRotationChanged, this.handleRotationChanged);
		document.addEventListener(CustomEventTypes.selectedObjectScaleChanged, this.handleScaleChanged);
		document.addEventListener(CustomEventTypes.onDisplayUnitChange, this.handleWorldUnitChanged);
		document.addEventListener(CustomEventTypes.updatePropertiesComponent, this.handlePropertyChanged);
		this.initializeValues();
	}

	componentWillUnmount()
	{
		document.removeEventListener(CustomEventTypes.hierarchyChangedEvent, this.handleHierarchyChanged);
		document.removeEventListener(CustomEventTypes.selectedObjectPositionChanged, this.handlePositionChanged);
		document.removeEventListener(CustomEventTypes.selectedObjectRotationChanged, this.handleRotationChanged);
		document.removeEventListener(CustomEventTypes.selectedObjectScaleChanged, this.handleScaleChanged);
		document.removeEventListener(CustomEventTypes.onDisplayUnitChange, this.handleWorldUnitChanged);
		document.removeEventListener(CustomEventTypes.updatePropertiesComponent, this.handlePropertyChanged);
	}

	componentDidUpdate(prevProps, prevState, snapshot)
	{
		if (!areArraysEqual(this.props.objects, this.objects))
		{
			this.objects = this.props.objects;
			this.initializeValues();
		}
	}

	initializeValues()
	{
		if (this.objects.length > 0)
		{
			this.displayScale = true;
			for (const object of this.objects)
			{
				if (object instanceof ARTracker) this.displayScale = false;
			}
			this.initializePostionValues();
			this.initializeRotationValues();
			this.initializeScaleValues();
		}
	}

	initializePostionValues()
	{
		if (!this.objects || !this.objects.length) return;

		this.positionMultivalue = {};
		this.initialPositions.length = 0;
		this.initialPositions.push(new THREE.Vector3().copy(this.objects[0].position));

		const px = this.objects[0].position.x;
		const py = this.objects[0].position.y;
		const pz = this.objects[0].position.z;
		this.onlyEmptyObjectsSelected = this.objects[0].geometry === undefined;

		for (let i = 1; i < this.objects.length; i++)
		{
			if (px !== this.objects[i].position.x) this.positionMultivalue.x = true;
			if (py !== this.objects[i].position.y) this.positionMultivalue.y = true;
			if (pz !== this.objects[i].position.z) this.positionMultivalue.z = true;
			if(this.objects[i].geometry) this.onlyEmptyObjectsSelected = false;
			this.initialPositions.push(new THREE.Vector3().copy(this.objects[i].position));
		}

		this.setState({ position: [px / Engine.getInstance().getDisplayUnitScale(), py / Engine.getInstance().getDisplayUnitScale(), pz / Engine.getInstance().getDisplayUnitScale()] });
	}

	initializeRotationValues()
	{
		if (!this.objects || !this.objects.length) return;

		this.rotationMultivalue = {};
		this.initialRotations.length = 0;
		this.initialRotations.push(new THREE.Euler().copy(this.objects[0].rotation));

		const rx = this.objects[0].rotation.x;
		const ry = this.objects[0].rotation.y;
		const rz = this.objects[0].rotation.z;

		for (let i = 1; i < this.objects.length; i++)
		{
			if (rx !== this.objects[i].rotation.x) this.rotationMultivalue.x = true;
			if (ry !== this.objects[i].rotation.y) this.rotationMultivalue.y = true;
			if (rz !== this.objects[i].rotation.z) this.rotationMultivalue.z = true;

			this.initialRotations.push(new THREE.Euler().copy(this.objects[i].rotation));
		}

		this.setState({
			rotation: [typeof rx === 'number' ? THREE.MathUtils.radToDeg(rx) : rx,
				typeof ry === 'number' ? THREE.MathUtils.radToDeg(ry) : ry,
				typeof rz === 'number' ? THREE.MathUtils.radToDeg(rz) : rz]
		});
		this.updateInteractiveModeCameraRotationLock();
	}

	initializeScaleValues()
	{
		if (!this.objects || !this.objects.length) return;

		this.scaleMultivalue = {};
		this.initialScales.length = 0;
		this.initialScales.push(new THREE.Vector3().copy(this.objects[0].scale));

		const sx = this.objects[0].scale.x;
		const sy = this.objects[0].scale.y;
		const sz = this.objects[0].scale.z;

		for (let i = 1; i < this.objects.length; i++)
		{
			if (sx !== this.objects[i].scale.x) this.scaleMultivalue.x = true;
			if (sy !== this.objects[i].scale.y) this.scaleMultivalue.y = true;
			if (sz !== this.objects[i].scale.z) this.scaleMultivalue.z = true;

			this.initialScales.push(new THREE.Vector3().copy(this.objects[i].scale));
		}

		this.setState({ scale: [sx, sy, sz] });
	}

	onPositionChanged({ x, y, z })
	{
		for (let i = 0; i < this.objects.length; i++)
		{
			if (typeof x === 'number' && !isNaN(x) && this.state.position[0] !== x)
			{
				this.objects[i].position.x = x;
				this.positionMultivalue.x = false;
			}
			if (typeof y === 'number' && !isNaN(y) && this.state.position[1] !== y)
			{
				this.objects[i].position.y = y;
				this.positionMultivalue.y = false;
			}
			if (typeof z === 'number' && !isNaN(z) && this.state.position[2] !== z)
			{
				this.objects[i].position.z = z;
				this.positionMultivalue.y = false;
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectPositionChanged, { detail: { sender: this } }));
	}

	onPositionCommitted()
	{
		if (store.getState().timelineModule.editorData.isRecording)
		{
			const eventDetail = { animationType: TimelineEffectorTypes.TransformTranslate, space: Space.Local };
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: eventDetail }));
		}
		else
		{
			const newPositions: THREE.Vector3[] = [];
			for (let i = 0; i < this.objects.length; i++)
			{
				newPositions.push(new THREE.Vector3().copy(this.objects[i].position));
				this.objects[i].position.copy(this.initialPositions[i]);
			}

			new SetObjectPositionAction(this.objects, newPositions, [...this.initialPositions]);
		}
	}

	onTranslateWithoutChildrenGizmoControlMouseDown()
	{
		for(const object of this.objects)
		{
			for (const child of object.children)
			{
				if (!(child instanceof Callout))
				{
					const worldPos = new THREE.Vector3();
					child.getWorldPosition(worldPos);
					childrenWorldPositions.set(child, worldPos);
				}
			}
		}
	}

	onTranslateWithoutChildrenGizmoControlMouseMove(deltaOffset)
	{
		for(let i = 0; i < this.objects.length; i++)
		{
			const object = this.objects[i];
			const parentWorldScale = new THREE.Vector3();
			object.parent.getWorldScale(parentWorldScale);

			const inverseParentWorldRotation = new THREE.Quaternion();
			object.parent.getWorldQuaternion(inverseParentWorldRotation);
			inverseParentWorldRotation.invert();

			const offset = new THREE.Vector3(deltaOffset.x, deltaOffset.y, deltaOffset.z);
			offset.applyQuaternion(inverseParentWorldRotation);
			offset.divide(parentWorldScale);
			object.position.copy(this.initialPositions[i]).add(offset);
		}

		childrenWorldPositions.forEach(function(worldPosition, child) {	TransformUtils.setWorldPosition(child, worldPosition);});

		this.setState({ position: [this.objects[0].position.x / Engine.getInstance().getDisplayUnitScale(), this.objects[0].position.y / Engine.getInstance().getDisplayUnitScale(), this.objects[0].position.z / Engine.getInstance().getDisplayUnitScale()] });
		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectPositionChanged, { detail: { sender: this } }));
	}

	onTranslateWithoutChildrenGizmoControlMouseUp()
	{
		const newPositions: THREE.Vector3[] = [];
		for(let i = 0; i < this.objects.length; i++)
		{
			const object = this.objects[i];
			newPositions.push(new THREE.Vector3().copy(object.position));
			object.position.copy(this.initialPositions[i]);
		}

		childrenWorldPositions.forEach(function(worldPosition, child) {	TransformUtils.setWorldPosition(child, worldPosition);});


		new TranslateObjectWithoutChildrenAction(this.objects, newPositions, [...this.initialPositions]);
		childrenWorldPositions.clear();
	}

	onRotationChanged({ x, y, z })
	{
		for (let i = 0; i < this.objects.length; i++)
		{
			if (typeof x === 'number' && !isNaN(x) && this.state.rotation[0] !== x)
			{
				this.objects[i].rotation.x = THREE.MathUtils.degToRad(x);
				this.rotationMultivalue.x = false;
			}
			if (typeof y === 'number' && !isNaN(y) && this.state.rotation[1] !== y)
			{
				this.objects[i].rotation.y = THREE.MathUtils.degToRad(y);
				this.rotationMultivalue.y = false;
			}
			if (typeof z === 'number' && !isNaN(z) && this.state.rotation[2] !== z)
			{
				this.objects[i].rotation.z = THREE.MathUtils.degToRad(z);
				this.rotationMultivalue.y = false;
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectRotationChanged, { detail: { sender: this } }));
	}

	onRotationCommitted()
	{
		if (store.getState().timelineModule.editorData.isRecording)
		{
			const eventDetail = { animationType: TimelineEffectorTypes.TransformRotateSelf };
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: eventDetail }));
		}
		else
		{
			const newRotations:THREE.Euler[] = [];
			for (let i = 0; i < this.objects.length; i++)
			{
				newRotations.push(new THREE.Euler().copy(this.objects[i].rotation));
				this.objects[i].rotation.copy(this.initialRotations[i]);
			}
			new SetObjectRotationAction(this.objects, newRotations, [...this.initialRotations]);
		}
	}

	onScaleChanged({ x, y, z })
	{
		for (let i = 0; i < this.objects.length; i++)
		{
			if (typeof x === 'number' && !isNaN(x) && this.state.scale[0] !== x)
			{
				this.objects[i].scale.x = x;
				this.scaleMultivalue.x = false;
			}
			if (typeof y === 'number' && !isNaN(y) && this.state.scale[1] !== y)
			{
				this.objects[i].scale.y = y;
				this.scaleMultivalue.y = false;
			}
			if (typeof z === 'number' && !isNaN(z) && this.state.scale[2] !== z)
			{
				this.objects[i].scale.z = z;
				this.scaleMultivalue.y = false;
			}
		}

		document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectScaleChanged, { detail: { sender: this } }));
	}

	onScaleCommitted()
	{
		if (store.getState().timelineModule.editorData.isRecording)
		{
			const eventDetail = { animationType: TimelineEffectorTypes.TransformScale };
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onTransformRecordEvent, { detail: eventDetail }));
		}
		else
		{
			const newScales: THREE.Vector3[] = [];
			for (let i = 0; i < this.objects.length; i++)
			{
				newScales.push(new THREE.Vector3().copy(this.objects[i].scale));
				this.objects[i].scale.copy(this.initialScales[i]);
			}
			new SetObjectScaleAction(this.objects, newScales, [...this.initialScales], null, null);
		}
	}

	copy()
	{
		const transform = { position: new THREE.Vector3().copy(this.objects[0].position), rotation: new THREE.Euler().copy(this.objects[0].rotation), scale: new THREE.Vector3().copy(this.objects[0].scale) };
		store.dispatch(StoreActions.application.setPropertyClipboard({ type: this.propertyType, data: transform }));
	}

	paste()
	{
		const clipboard = store.getState().applicationModule.propertyClipboard;
		if (!clipboard || clipboard.type !== this.propertyType) return;

		const newPositions: THREE.Vector3[] = [];
		const newRotations: THREE.Euler[] = [];
		const newScales: THREE.Vector3[] = [];
		for (let i = 0; i < this.objects.length; i++)
		{
			newPositions.push(clipboard.data.position);
			newRotations.push(clipboard.data.rotation);
			newScales.push(clipboard.data.scale);
			this.objects[i].position.copy(this.initialPositions[i]);
			this.objects[i].rotation.copy(this.initialRotations[i]);
			this.objects[i].scale.copy(this.initialScales[i]);
		}
		new SetObjectTransformAction(this.objects, newPositions, [...this.initialPositions], newRotations, [...this.initialRotations], newScales, [...this.initialScales]);

	}

	isMultiValues()
	{
		const posString = JSON.stringify(this.positionMultivalue);
		const rotString = JSON.stringify(this.rotationMultivalue);
		const scaleString = JSON.stringify(this.scaleMultivalue);
		if (posString.toLocaleLowerCase().includes('true') || rotString.toLocaleLowerCase().includes('true') || scaleString.toLocaleLowerCase().includes('true')) return true;
		return false;
	}

	areOnlyCameraObjectsSelected()
	{
		for (const object of this.objects)
		{
			if (!(object instanceof ScopeCamera)) return false;
		}
		return true;
	}

	renderContent()
	{
		return (
			<>
				<SyncedProperty data-name={"property-position"}
					objects={this.objects}
					getterFunc={(o) => { return o.position; }}
					comparerFunc={MathUtils.areVectorsEqual}
					resyncFunction={(targets, defValues, curValues) =>
					{
						new SetObjectPositionAction(targets, defValues, curValues);
					}}
				>
					<Vector3Property data-name={"property-position"}
						label="Position"
						value={this.state.position}
						defaultValue={{ x: 0, y: 0, z: 0 }}
						multivalues={this.positionMultivalue}
						readOnly={this.props.readOnly}
						unitScaling={true}
						step={this.props.snapSettings.translation}
						onChangeCallback={this.onPositionChanged}
						onCommitCallback={this.onPositionCommitted}
						decimals={3}
						data-disabled={this.state.positionLocked}
						enableGizmoController={this.onlyEmptyObjectsSelected}//this.objects.length === 1 && !this.objects[0].geometry}
						gizmoControlMouseDownCallback={this.onTranslateWithoutChildrenGizmoControlMouseDown}
						gizmoControlMouseMoveCallback={this.onTranslateWithoutChildrenGizmoControlMouseMove}
						gizmoControlMouseUpCallback={this.onTranslateWithoutChildrenGizmoControlMouseUp}
						gizmoControlTooltipOverride={TooltipKeys.TranslateWithoutChildren}
						contextMenuOverride={
							[
								{
									renderObject: "Reset values",
									id: "Reset to default",
									callback: () =>
									{
										this.onPositionChanged({ x: 0, y: 0, z: 0 });
										this.onPositionCommitted();
									},
								},
							]
						}
					/>
				</SyncedProperty>

				<SyncedProperty data-name={"property-rotation"}
					objects={this.objects}
					getterFunc={(o) => { return o.rotation; }}
					comparerFunc={MathUtils.areVectorsEqual}
					resyncFunction={(targets, defValues, curValues) =>
					{
						new SetObjectRotationAction(targets, defValues, curValues);
					}}
				>
					<Vector3Property data-name={"property-rotation"}
						label="Rotation"
						value={this.state.rotation}
						defaultValue={{ x: 0, y: 0, z: 0 }}
						step={this.props.snapSettings.rotation}
						multivalues={this.rotationMultivalue}
						readOnly={this.props.readOnly}
						onChangeCallback={this.onRotationChanged}
						onCommitCallback={this.onRotationCommitted}
						decimals={1}
						data-disabled={this.state.rotationLocked}
						contextMenuOverride={
							[
								{
									renderObject: "Reset values",
									id: "Reset to default",
									callback: () =>
									{
										this.onRotationChanged({ x: 0, y: 0, z: 0 });
										this.onRotationCommitted();
									},
								},
							]
						}
					/>
				</SyncedProperty>

				{
					!this.displayScale ? null :
						<SyncedProperty data-name={"property-scale"}
							objects={this.objects}
							getterFunc={(o) => { return o.scale; }}
							comparerFunc={MathUtils.areVectorsEqual}
							resyncFunction={(targets, defValues, curValues) =>
							{
								new SetObjectScaleAction(targets, defValues, curValues, null, null);
							}}
						>
							<Vector3Property data-name={"property-scale"}
								label="Scale"
								value={this.state.scale}
								defaultValue={{ x: 1, y: 1, z: 1 }}
								step={this.props.snapSettings.scale}
								multivalues={this.scaleMultivalue}
								readOnly={this.props.readOnly}
								onChangeCallback={this.onScaleChanged}
								onCommitCallback={this.onScaleCommitted}
								canBeLinked={true}
								decimals={4}
								data-disabled={this.state.scaleLocked}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												this.onScaleChanged({ x: 1, y: 1, z: 1 });
												this.onScaleCommitted();
											},
										},
									]
								}
							/>
						</SyncedProperty>
				}
			</>
		);
	}

	render()
	{
		return (
			this.objects.length === 0 ? null :
				this.props.noGroup ? this.renderContent() :
					<PropertyGroup
						title="Transform"
						decorators={[
							...(this.areOnlyCameraObjectsSelected() ?
								[
									<div className={"button addon"} key={k++}>
										<CameraImport data-name={"apply-camera-to-view"}
											tooltip={TooltipKeys.ApplyToSceneView}
											onClick={() =>
											{
												const sceneCamera = this.objects[0];
												sceneCamera.updateWorldMatrix(true, false);
												const mainSceneView = Engine.getInstance().getMainSceneView();
												if(mainSceneView)
												{
													const cameraController = mainSceneView.cameraController;
													const cameraWorldPosition = new THREE.Vector3();
													const cameraWorldRotation = new THREE.Quaternion();

													sceneCamera.getWorldPosition(cameraWorldPosition);
													sceneCamera.getWorldQuaternion(cameraWorldRotation);
													const tempEuler = new THREE.Euler().setFromQuaternion(cameraWorldRotation, "YXZ");
													tempEuler.set(tempEuler.x, tempEuler.y, 0, "YXZ");
													cameraWorldRotation.setFromEuler(tempEuler);

													cameraController.setTransformAnimation(cameraWorldPosition, cameraWorldRotation, false);
													const cameraForward = new THREE.Vector3();
													sceneCamera.perspectiveCamera.getWorldDirection(cameraForward);
													cameraController.anchor.addVectors(cameraWorldPosition, cameraForward);
												}
											}}
										/>
									</div>,
									<Spacer size={{ x: "6px" }} key={k++} />,
									<div className={"button addon"} key={k++}>
										<CameraExport data-name={"align-camera-to-view"}
											tooltip={TooltipKeys.AlignToSceneView}
											onClick={() =>
											{
												const mainSceneView = Engine.getInstance().getMainSceneView();
												if(mainSceneView)
												{
													new AlignToViewAction(this.objects, mainSceneView.activeCamera, false);
												}
											}}
										/>
									</div>,
								] : []
							)
						]}
						onContextMenu={(e) =>
						{
							e.preventDefault();
							ContextMenu.show(e, [], "Transform", [
								{
									items:
											[
												...(!this.isMultiValues() ? [{
													renderObject: "Copy",
													id: "copy",
													callback: this.copy,
												}] : []),
												...(store.getState().applicationModule.propertyClipboard?.type === this.propertyType && !this.props.readOnly ? [{
													renderObject: "Paste",
													id: "paste",
													callback: this.paste,
												}] : [])
											],
								},
							]);
						}}
					>
						{this.renderContent()}
					</PropertyGroup >
		);
	}
}

const mapStateToProps = (state) =>
{
	return {
		snapSettings: state.applicationModule.snapSettings,
	};
};

export default connect(mapStateToProps)(TransformComponent);
